"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClaimsVerifier = void 0;
const sjcl_1 = require("sjcl");
const did_resolver_interface_1 = require("@ew-did-registry/did-resolver-interface");
const crypto_1 = __importDefault(require("crypto"));
const claims_1 = require("../claims");
class ClaimsVerifier extends claims_1.Claims {
    /**
     * Verifies integrity of the claim, the claim is issued by the user
     *  delegate and the authenticity of the issuer's signature
     *
     * @example
     * ```typescript
     * import { ClaimsVerifier } from '@ew-did-registry/claims';
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const keys = new Keys();
     * const claims = new ClaimsVerifier(verifier);
     * const verified = claims.verifyPublicProof(issuedToken);
     * ```
     * @param { string } token containing proof data
     * @returns { Promise<void> } whether the proof was succesfull
     * @throws if the proof failed
     */
    verifyPublicProof(claimUrl, { holderDoc, issuerDoc, } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.verify(claimUrl, {
                holderDoc,
                issuerDoc,
            });
        });
    }
    /**
     * Checks issuer signature on issued token and user signature on proof token
     * and verifies that proof and private data mathches to each other
     *
     * @example
     * ```typescript
     * import { ClaimsVerifier } from '@ew-did-registry/claims';
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const keys = new Keys();
     * const claims = new ClaimsVerifier(verifier);
     * const verified = claims.verifyPrivateProof(proofToken);
     * ```
     * @param { string } proofToken contains proof data
     * @param { string } privateToken contains private data
     * @returns { Promise<void> } whether the proof was succesfull
     * @throws if the proof failed
     */
    verifyPrivateProof(proofToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const { claimUrl } = this.jwt.decode(proofToken);
            const privateClaim = yield this.verify(claimUrl);
            const curve = sjcl_1.ecc.curves.k256;
            const g = curve.G;
            const proofClaim = this.jwt.decode(proofToken);
            if (!this.document.isValidDelegate(did_resolver_interface_1.DelegateTypes.verification, privateClaim.signer, privateClaim.did)) {
                throw new Error("Issuer isn't a user delegate");
            }
            const { proofData } = proofClaim;
            // eslint-disable-next-line no-restricted-syntax
            Object.entries(privateClaim.claimData).forEach(([key, value]) => {
                const field = proofData[key];
                if (field.encrypted) {
                    const PK = curve.fromBits(value);
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    let { h, s } = field.value;
                    h = curve.fromBits(h);
                    s = sjcl_1.bn.fromBits(s);
                    const c = sjcl_1.bn.fromBits(sjcl_1.hash.sha256.hash(g.x.toBits().concat(h.toBits()).concat(PK.toBits())));
                    const left = g.mult(s);
                    const right = PK.mult(c).toJac().add(h).toAffine();
                    if (!sjcl_1.bitArray.equal(left.toBits(), right.toBits())) {
                        throw new Error("User didn't prove the knowledge of the private data");
                    }
                }
                else {
                    const fieldHash = crypto_1.default
                        .createHash('sha256')
                        .update(field.value)
                        .digest('hex');
                    // eslint-disable-next-line new-cap
                    const PK = g.mult(new sjcl_1.bn(fieldHash));
                    const bitsPK = PK.toBits();
                    if (!sjcl_1.bitArray.equal(value, bitsPK)) {
                        throw new Error('Disclosed field does not correspond to stored field');
                    }
                }
            });
        });
    }
}
exports.ClaimsVerifier = ClaimsVerifier;
//# sourceMappingURL=claimsVerifier.js.map